import { Helmet } from 'react-helmet-async';
import { Phone, AlertTriangle, DollarSign, Clock, Shield, CheckCircle2, Sun, Droplets, Wind, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MetalRoofs = () => {
  // Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Metal Roof Coating",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Savage Rejuvenation Coatings",
      "telephone": "+1-321-804-4413"
    },
    "areaServed": "Central Florida",
    "description": "Professional metal roof coating and restoration services in Orlando, Tampa, and Central Florida. Prevent rust, reduce heat, and extend roof life."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://savagerejuvenationcoatings.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Metal Roofs",
        "item": "https://savagerejuvenationcoatings.com/roofs/metal"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Metal Roof Problems & Coating Solutions | Orlando Tampa FL | Savage Rejuvenation</title>
        <meta name="description" content="Discover metal roof vulnerabilities in Florida's climate. Get repair costs, rust prevention, and learn how protective coating can save your metal roof. Call (321) 804-4413." />
        <meta name="keywords" content="metal roof problems Florida, metal roof rust prevention Orlando, metal roof coating Tampa, metal roof repair cost, Florida metal roof protection" />
        
        <link rel="canonical" href="https://savagerejuvenationcoatings.com/roofs/metal" />
        
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-secondary border-b-4 border-primary">
          <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 flex items-center justify-between">
            <nav className="hidden lg:flex items-center gap-6">
              <a href="/" className="text-white hover:text-primary font-manrope font-semibold">Home</a>
              <a href="/reviews" className="text-white hover:text-primary font-manrope font-semibold">Reviews</a>
              <a href="/qa" className="text-white hover:text-primary font-manrope font-semibold">Q&A</a>
              <a href="/blog" className="text-white hover:text-primary font-manrope font-semibold">Blog</a>
            </nav>
            <div className="hidden lg:flex items-center gap-6">
              <a href="tel:3218044413" className="text-white hover:text-primary transition-colors flex items-center gap-2 text-lg font-manrope font-semibold">
                <Phone className="h-5 w-5" />
                (321) 804-4413
              </a>
            </div>
            <div className="lg:hidden">
              <a href="tel:3218044413">
                <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[2px_2px_0px_0px_#000] rounded-sm px-3 py-2">
                  <Phone className="h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section 
          className="relative min-h-[500px] flex items-center bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1653889676142-9832054b9911?crop=entropy&cs=srgb&fm=jpg&q=85')`
          }}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-white">
            <h1 className="font-barlow text-4xl md:text-6xl font-bold uppercase mb-6">
              Metal Roofs
            </h1>
            <p className="text-xl md:text-2xl font-manrope max-w-3xl mb-8">
              Durable and long-lasting—but not immune to Florida's unique challenges. 
              Learn how to protect your metal roof investment.
            </p>
            <a href="tel:3218044413">
              <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[4px_4px_0px_0px_#000] rounded-sm text-lg px-8 py-6 font-barlow uppercase font-bold">
                <Phone className="mr-2 h-5 w-5" />
                Get FREE Inspection
              </Button>
            </a>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-8 text-center">
              About Metal Roofs
            </h2>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="font-manrope text-lg text-gray-700 mb-6">
                  Metal roofing is increasingly popular in Florida, known for its durability and 
                  <strong className="text-primary"> 40-70 year lifespan</strong> when properly maintained. 
                  Available in steel, aluminum, copper, and zinc, metal roofs offer excellent wind resistance 
                  and fire protection.
                </p>
                <p className="font-manrope text-lg text-gray-700 mb-6">
                  However, Florida's salt air (especially near coasts), high humidity, and intense sun create 
                  unique challenges that can compromise your metal roof's integrity without proper protection.
                </p>
                <div className="bg-accent-light border-2 border-black p-6">
                  <h3 className="font-barlow text-xl font-bold uppercase mb-3">Quick Facts</h3>
                  <ul className="space-y-2 font-manrope">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Average lifespan: 40-70 years (with maintenance)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Wind resistance: Up to 140+ mph</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Energy efficient - reflects solar radiation</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Rust is #1 enemy in Florida's climate</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1653889676142-9832054b9911?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Metal roof on commercial building"
                  className="w-full border-4 border-primary shadow-[8px_8px_0px_0px_#000]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Problems Section */}
        <section className="py-16 bg-red-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-4">
                <span className="text-red-600">Weather Vulnerabilities</span> in Florida
              </h2>
              <p className="font-manrope text-lg text-gray-700 max-w-3xl mx-auto">
                While metal roofs are tough, Florida's environment presents specific threats:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white border-2 border-red-300 shadow-[4px_4px_0px_0px_#DC2626] p-6">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Droplets className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-3">Rust & Corrosion</h3>
                <p className="font-manrope text-gray-700 mb-4">
                  The #1 enemy of metal roofs in Florida:
                </p>
                <ul className="space-y-2 font-manrope text-gray-600">
                  <li>• Salt air accelerates oxidation (coastal areas)</li>
                  <li>• Standing water causes rust spots</li>
                  <li>• Scratches expose raw metal to elements</li>
                  <li>• Fastener rust leads to leaks</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-red-300 shadow-[4px_4px_0px_0px_#DC2626] p-6">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Sun className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-3">Thermal Expansion</h3>
                <p className="font-manrope text-gray-700 mb-4">
                  Metal expands and contracts with temperature changes:
                </p>
                <ul className="space-y-2 font-manrope text-gray-600">
                  <li>• Seam separation over time</li>
                  <li>• Fastener loosening ("oil canning")</li>
                  <li>• Panel warping and buckling</li>
                  <li>• Seal and caulk failure</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-red-300 shadow-[4px_4px_0px_0px_#DC2626] p-6">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Zap className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-3">Coating Degradation</h3>
                <p className="font-manrope text-gray-700 mb-4">
                  Factory coatings break down in Florida's UV:
                </p>
                <ul className="space-y-2 font-manrope text-gray-600">
                  <li>• Paint chalking and fading</li>
                  <li>• Loss of reflective properties</li>
                  <li>• Exposed metal = rapid rust</li>
                  <li>• Increased heat absorption</li>
                </ul>
              </div>
            </div>

            <div className="mt-12 bg-red-600 text-white p-8 border-2 border-black shadow-[6px_6px_0px_0px_#000]">
              <div className="flex items-center gap-4 mb-4">
                <AlertTriangle className="h-10 w-10" />
                <h3 className="font-barlow text-2xl font-bold uppercase">Damage Statistics in Florida</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-5xl font-barlow font-bold">70%</p>
                  <p className="font-manrope">of uncoated metal roofs show rust within 10 years</p>
                </div>
                <div>
                  <p className="text-5xl font-barlow font-bold">50%</p>
                  <p className="font-manrope">coating degradation in coastal areas within 8 years</p>
                </div>
                <div>
                  <p className="text-5xl font-barlow font-bold">$5K+</p>
                  <p className="font-manrope">average rust damage repair cost</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-4">
                <DollarSign className="inline h-10 w-10 text-primary" /> Repair & Replacement Costs
              </h2>
              <p className="font-manrope text-lg text-gray-700 max-w-3xl mx-auto">
                Understanding the true cost of metal roof maintenance in Central Florida
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gray-50 border-2 border-black shadow-[4px_4px_0px_0px_#000] p-8">
                <h3 className="font-barlow text-2xl font-bold uppercase mb-6 text-red-600">
                  Repair Costs
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Rust spot treatment (per area)</span>
                    <span className="font-barlow font-bold text-xl">$200 - $500</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Panel replacement (each)</span>
                    <span className="font-barlow font-bold text-xl">$400 - $1,200</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Seam/fastener repair</span>
                    <span className="font-barlow font-bold text-xl">$150 - $400</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Leak repair</span>
                    <span className="font-barlow font-bold text-xl">$300 - $800</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Major rust damage repair</span>
                    <span className="font-barlow font-bold text-xl">$2,000 - $8,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-manrope font-bold">Professional cleaning & treatment</span>
                    <span className="font-barlow font-bold text-xl">$300 - $700</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border-2 border-black shadow-[4px_4px_0px_0px_#000] p-8">
                <h3 className="font-barlow text-2xl font-bold uppercase mb-6 text-red-600">
                  Full Replacement Costs
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Steel roofing (per sq ft)</span>
                    <span className="font-barlow font-bold text-xl">$7 - $14</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Aluminum roofing (per sq ft)</span>
                    <span className="font-barlow font-bold text-xl">$9 - $16</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Standing seam (per sq ft)</span>
                    <span className="font-barlow font-bold text-xl">$12 - $22</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Average home (2,000 sq ft)</span>
                    <span className="font-barlow font-bold text-xl">$18,000 - $40,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-manrope">Removal & disposal</span>
                    <span className="font-barlow font-bold text-xl">$1,500 - $3,000</span>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-red-100 border-2 border-red-300">
                  <p className="font-manrope text-red-800">
                    <strong>Florida Average:</strong> $20,000 - $45,000 for full metal roof replacement
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16 bg-accent-light">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-4">
                The <span className="text-primary">Coating Solution</span>
              </h2>
              <p className="font-manrope text-lg text-gray-700 max-w-3xl mx-auto">
                Professional metal roof coating creates a protective barrier that prevents rust, 
                reduces heat, and extends your roof's life by decades.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">Rust Prevention</h3>
                    <p className="font-manrope text-gray-700">Our coating seals exposed metal, stopping oxidation and preventing new rust from forming.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Sun className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">Heat Reflection</h3>
                    <p className="font-manrope text-gray-700">Reflective coating reduces surface temperature by up to 50°F, cutting cooling costs significantly.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">15-20 Year Extension</h3>
                    <p className="font-manrope text-gray-700">Properly coated metal roofs can last 60+ years with periodic recoating every 10-15 years.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">80% Cost Savings</h3>
                    <p className="font-manrope text-gray-700">Coating costs $3-6 per sq ft vs. $12-22 for replacement. Massive savings!</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-black shadow-[6px_6px_0px_0px_#FF8C00] p-8">
                <h3 className="font-barlow text-2xl font-bold uppercase mb-6 text-center">
                  Coating vs. Replacement
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-manrope">Average coating cost (2,000 sq ft)</span>
                    <span className="font-barlow font-bold text-xl text-green-600">$6,000 - $12,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-manrope">Average replacement cost</span>
                    <span className="font-barlow font-bold text-xl text-red-600">$20,000 - $45,000</span>
                  </div>
                  <div className="border-t-2 border-primary pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-manrope font-bold text-lg">Your Savings</span>
                      <span className="font-barlow font-bold text-2xl text-primary">$14,000 - $33,000</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-secondary text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase mb-6">
              Protect Your Metal Roof Investment
            </h2>
            <p className="text-xl font-manrope mb-8">
              Get a FREE inspection and quote. We'll assess your metal roof's condition and provide 
              honest recommendations—no pressure, just expert advice.
            </p>
            <a href="tel:3218044413">
              <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-white shadow-[4px_4px_0px_0px_#fff] rounded-sm text-xl px-10 py-7 font-barlow uppercase font-bold">
                <Phone className="mr-2 h-6 w-6" />
                Call Now: (321) 804-4413
              </Button>
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black text-white py-8">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <p className="font-manrope text-gray-400">
              © 2026 Savage Rejuvenation Coatings. All Rights Reserved. | Serving Orlando, Tampa & Central Florida
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default MetalRoofs;
